import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import saasPlatformService from '../../services/saasPlatformService';
import './SaasPlatform.css';

const SaasWalletAndUsage = () => {
  const [tenants, setTenants] = useState([]);
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [wallet, setWallet] = useState(null);
  const [ledger, setLedger] = useState([]);
  const [usage, setUsage] = useState(null);
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  // Top-Up Modal State
  const [showTopupModal, setShowTopupModal] = useState(false);
  const [topupForm, setTopupForm] = useState({
    amount: 1000,
    transactionType: 'PURCHASE',
    reason: 'Monthly diagnostic credit refill package',
    idempotencyKey: ''
  });
  const [topupSubmitting, setTopupSubmitting] = useState(false);

  // Payment Recording Modal State
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [paymentForm, setPaymentForm] = useState({
    amountPaid: 0,
    paymentMethod: 'BANK_TRANSFER',
    transactionRef: ''
  });
  const [paymentSubmitting, setPaymentSubmitting] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    const fetchTenants = async () => {
      setLoading(true);
      try {
        const res = await saasPlatformService.getTenants();
        const list = res.data || res || [];
        setTenants(list);
        if (list.length > 0) {
          const defaultTenant = list.find(t => t.tenantId === 9111) || list[0];
          setSelectedTenantId(defaultTenant.tenantId);
        }
      } catch (err) {
        console.error('Failed to load tenants', err);
        setError('Failed to load tenants list.');
      } finally {
        setLoading(false);
      }
    };
    fetchTenants();
  }, []);

  const loadTenantFinancials = async (tenantId) => {
    if (!tenantId) return;
    setError(null);
    try {
      const [walletRes, ledgerRes, usageRes, invoicesRes] = await Promise.all([
        saasPlatformService.getTenantWallet(tenantId).catch(() => null),
        saasPlatformService.getTenantWalletLedger(tenantId).catch(() => []),
        saasPlatformService.getTenantUsage(tenantId).catch(() => null),
        saasPlatformService.getTenantInvoices(tenantId).catch(() => [])
      ]);

      setWallet(walletRes?.data || walletRes);
      setLedger(ledgerRes?.data || ledgerRes || []);
      setUsage(usageRes?.data || usageRes);
      setInvoices(invoicesRes?.data || invoicesRes || []);
    } catch (err) {
      console.error('Failed to load tenant financial details', err);
      setError('Failed to load tenant wallet or usage ledger.');
    }
  };

  useEffect(() => {
    if (selectedTenantId) {
      loadTenantFinancials(selectedTenantId);
    }
  }, [selectedTenantId]);

  const handleOpenTopup = () => {
    const randomId = 'TOP-' + Date.now().toString(36).toUpperCase() + '-' + Math.random().toString(36).substring(2, 6).toUpperCase();
    setTopupForm({
      amount: 1000,
      transactionType: 'PURCHASE',
      reason: 'Platform prepaid diagnostic usage credit pack',
      idempotencyKey: randomId
    });
    setShowTopupModal(true);
  };

  const handleTopupSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTenantId) return;
    setTopupSubmitting(true);
    setError(null);
    try {
      const payload = {
        amount: parseFloat(topupForm.amount),
        transactionType: topupForm.transactionType,
        reason: topupForm.reason,
        idempotencyKey: topupForm.idempotencyKey
      };

      const res = await saasPlatformService.topupTenantWallet(selectedTenantId, payload);
      setSuccess(`Top-up of ${topupForm.amount} credits completed successfully! New balance: ${res.data?.newBalance ?? res?.newBalance}`);
      setShowTopupModal(false);
      loadTenantFinancials(selectedTenantId);
    } catch (err) {
      console.error('Top-up failed', err);
      setError(err?.response?.data?.message || 'Top-up transaction failed.');
    } finally {
      setTopupSubmitting(false);
    }
  };

  const handleOpenPayment = (inv) => {
    setSelectedInvoice(inv);
    setPaymentForm({
      amountPaid: inv.amountDue || inv.totalAmount,
      paymentMethod: 'NEFT_RTGS',
      transactionRef: 'UTR-' + Date.now().toString(36).toUpperCase()
    });
    setShowPaymentModal(true);
  };

  const handlePaymentSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTenantId || !selectedInvoice) return;
    setPaymentSubmitting(true);
    setError(null);
    try {
      const invId = selectedInvoice.invoiceId || selectedInvoice.InvoiceID;
      await saasPlatformService.recordInvoicePayment(selectedTenantId, invId, {
        amountPaid: parseFloat(paymentForm.amountPaid),
        paymentMethod: paymentForm.paymentMethod,
        transactionRef: paymentForm.transactionRef
      });
      setSuccess(`Payment recorded for Invoice #${selectedInvoice.invoiceNumber || invId}!`);
      setShowPaymentModal(false);
      loadTenantFinancials(selectedTenantId);
    } catch (err) {
      console.error('Record payment failed', err);
      setError(err?.response?.data?.message || 'Payment recording failed.');
    } finally {
      setPaymentSubmitting(false);
    }
  };

  const currentTenantObj = tenants.find(t => t.tenantId === Number(selectedTenantId));

  return (
    <div className="saas-container">
      {/* Header */}
      <div className="saas-header">
        <div className="saas-header-title">
          <h1>
            <span>💰</span> Credits, Wallet, Metering &amp; Billing
          </h1>
          <p>Prepaid Usage Ledger, Atomic Top-ups, Idempotent Transactions &amp; Invoices</p>
        </div>
        <div className="saas-header-actions">
          <button className="saas-btn saas-btn-primary" onClick={handleOpenTopup} disabled={!wallet}>
            ⚡ Issue Top-Up
          </button>
          <button className="saas-btn saas-btn-secondary" onClick={() => navigate('/saas/tenants')}>
            ← Back to Tenants
          </button>
        </div>
      </div>

      {error && (
        <div className="saas-error-box">
          <div><strong>Financial Ledger Alert:</strong> {error}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setError(null)}>✕</button>
        </div>
      )}

      {success && (
        <div className="saas-success-box">
          <div><strong>Ledger Confirmation:</strong> {success}</div>
          <button className="saas-btn saas-btn-secondary" onClick={() => setSuccess(null)}>✕</button>
        </div>
      )}

      {/* Tenant Selector */}
      <div className="saas-card" style={{ marginBottom: '1.5rem', padding: '1rem 1.5rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '1rem' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem' }}>Select Client:</label>
            <select
              className="saas-select"
              value={selectedTenantId}
              onChange={(e) => setSelectedTenantId(Number(e.target.value))}
              style={{ minWidth: '320px', fontWeight: 600 }}
            >
              {tenants.map(t => (
                <option key={t.tenantId} value={t.tenantId}>
                  #{t.tenantId} — {t.tenantCode} ({t.organizationName})
                </option>
              ))}
            </select>
          </div>
          <button className="saas-btn saas-btn-secondary" onClick={() => loadTenantFinancials(selectedTenantId)}>
            🔄 Refresh Ledger
          </button>
        </div>
      </div>

      {/* Wallet Balance Summary Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '1.25rem', marginBottom: '2rem' }}>
        <div className="saas-card" style={{ borderLeft: '4px solid #0284c7', marginBottom: 0 }}>
          <div className="saas-card-body">
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>AVAILABLE CREDITS</span>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: '#0284c7', margin: '0.25rem 0' }}>
              {wallet ? Number(wallet.availableBalance ?? wallet.balance ?? 0).toLocaleString() : '—'}
            </div>
            <span style={{ fontSize: '0.75rem', color: '#10b981' }}>● Ready for diagnostic consumption</span>
          </div>
        </div>

        <div className="saas-card" style={{ borderLeft: '4px solid #10b981', marginBottom: 0 }}>
          <div className="saas-card-body">
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>TOTAL PURCHASED</span>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: '#10b981', margin: '0.25rem 0' }}>
              {wallet ? Number(wallet.lifetimeIssued ?? wallet.totalPurchased ?? 0).toLocaleString() : '—'}
            </div>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Cumulative lifetime top-ups</span>
          </div>
        </div>

        <div className="saas-card" style={{ borderLeft: '4px solid #f59e0b', marginBottom: 0 }}>
          <div className="saas-card-body">
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>TOTAL CONSUMED</span>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: '#f59e0b', margin: '0.25rem 0' }}>
              {wallet ? Number(wallet.lifetimeConsumed ?? wallet.totalConsumed ?? 0).toLocaleString() : '—'}
            </div>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Investigation &amp; SMS charges</span>
          </div>
        </div>

        <div className="saas-card" style={{ borderLeft: '4px solid #6366f1', marginBottom: 0 }}>
          <div className="saas-card-body">
            <span style={{ fontSize: '0.75rem', fontWeight: 600, color: '#64748b' }}>CURRENT PERIOD BILLING</span>
            <div style={{ fontSize: '2rem', fontWeight: 700, color: '#6366f1', margin: '0.25rem 0' }}>
              ₹{usage ? Number(usage.billableAmountInr).toLocaleString() : '—'}
            </div>
            <span style={{ fontSize: '0.75rem', color: '#64748b' }}>Period: {usage?.period || '2026-10'}</span>
          </div>
        </div>
      </div>

      {/* Two-Column: Transaction Ledger & Invoices */}
      <div style={{ display: 'grid', gridTemplateColumns: '1.2fr 1fr', gap: '1.5rem' }}>
        {/* Append-Only Credit Transaction Ledger */}
        <div className="saas-card">
          <div className="saas-card-header">
            <div>
              <h3 style={{ margin: 0 }}>Append-Only Credit Ledger ({ledger.length})</h3>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                Immutable audit trail with before/after balances &amp; idempotency references
              </span>
            </div>
          </div>
          <div className="saas-table-container">
            <table className="saas-table" style={{ fontSize: '0.8125rem' }}>
              <thead>
                <tr>
                  <th>Type</th>
                  <th>Amount</th>
                  <th>Balance Before / After</th>
                  <th>Actor / Reason</th>
                  <th>Reference</th>
                  <th>Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {ledger.length > 0 ? (
                  ledger.map((entry, idx) => (
                    <tr key={entry.ledgerId || idx}>
                      <td>
                        <span className={`saas-badge ${entry.transactionType === 'PURCHASE' ? 'saas-badge-active' : entry.transactionType === 'CONSUMPTION' ? 'saas-badge-warning' : 'saas-badge-info'}`}>
                          {entry.transactionType}
                        </span>
                      </td>
                      <td>
                        <strong style={{ color: Number(entry.amount) >= 0 ? '#10b981' : '#b91c1c' }}>
                          {Number(entry.amount) > 0 ? `+${entry.amount}` : entry.amount}
                        </strong>
                      </td>
                      <td style={{ fontFamily: 'monospace' }}>
                        {entry.beforeBalance} → <strong>{entry.afterBalance}</strong>
                      </td>
                      <td>
                        <div><strong>{entry.actorUsername || 'PlatformAdmin'}</strong></div>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>{entry.reason}</div>
                      </td>
                      <td style={{ fontFamily: 'monospace', fontSize: '0.75rem', color: '#64748b' }}>
                        {entry.idempotencyKey || entry.referenceNote || '—'}
                      </td>
                      <td style={{ color: '#64748b', fontSize: '0.75rem' }}>
                        {new Date(entry.createdAt).toLocaleString()}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                      No wallet transactions recorded for this client.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Invoices & Payments */}
        <div className="saas-card">
          <div className="saas-card-header">
            <div>
              <h3 style={{ margin: 0 }}>Client Invoices &amp; Settlements ({invoices.length})</h3>
              <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                Monthly subscription invoices &amp; settlement receipts
              </span>
            </div>
          </div>
          <div className="saas-table-container">
            <table className="saas-table" style={{ fontSize: '0.8125rem' }}>
              <thead>
                <tr>
                  <th>Invoice #</th>
                  <th>Period</th>
                  <th>Total / Paid</th>
                  <th>Status</th>
                  <th style={{ textAlign: 'right' }}>Action</th>
                </tr>
              </thead>
              <tbody>
                {invoices.length > 0 ? (
                  invoices.map((inv, idx) => (
                    <tr key={inv.invoiceId || idx}>
                      <td>
                        <strong style={{ color: '#0284c7' }}>{inv.invoiceNumber}</strong>
                        <div style={{ fontSize: '0.7rem', color: '#64748b' }}>
                          Issued: {new Date(inv.issuedDate).toLocaleDateString()}
                        </div>
                      </td>
                      <td>{inv.billingPeriod}</td>
                      <td>
                        <div>₹{Number(inv.totalAmount).toLocaleString()}</div>
                        <div style={{ fontSize: '0.7rem', color: '#10b981' }}>
                          Paid: ₹{Number(inv.amountPaid).toLocaleString()}
                        </div>
                      </td>
                      <td>
                        <span className={`saas-badge ${inv.status === 'PAID' ? 'saas-badge-active' : inv.status === 'OVERDUE' ? 'saas-badge-suspended' : 'saas-badge-warning'}`}>
                          ● {inv.status}
                        </span>
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        {inv.status !== 'PAID' ? (
                          <button
                            className="saas-btn saas-btn-success"
                            style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                            onClick={() => handleOpenPayment(inv)}
                          >
                            💳 Record Payment
                          </button>
                        ) : (
                          <span style={{ fontSize: '0.75rem', color: '#10b981', fontWeight: 600 }}>Settled ✓</span>
                        )}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" style={{ textAlign: 'center', padding: '2rem', color: '#94a3b8' }}>
                      No invoices found for this client.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Top-up Modal */}
      {showTopupModal && (
        <div className="saas-modal-backdrop" onClick={() => setShowTopupModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="saas-modal-header">
              <h3>Issue Diagnostic Credit Top-Up: #{selectedTenantId}</h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowTopupModal(false)}>✕</button>
            </div>
            <form onSubmit={handleTopupSubmit}>
              <div className="saas-modal-body">
                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Top-Up Credit Amount</label>
                  <input
                    type="number"
                    step="1"
                    min="1"
                    className="saas-form-input"
                    value={topupForm.amount}
                    onChange={(e) => setTopupForm({ ...topupForm, amount: e.target.value })}
                    required
                  />
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Current Available Balance: <strong>{wallet?.balance ?? 0} Credits</strong>
                  </span>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Transaction Type</label>
                  <select
                    className="saas-form-input"
                    value={topupForm.transactionType}
                    onChange={(e) => setTopupForm({ ...topupForm, transactionType: e.target.value })}
                  >
                    <option value="PURCHASE">PURCHASE (Client Payment Verified)</option>
                    <option value="PROMOTIONAL">PROMOTIONAL (Admin Grant)</option>
                    <option value="ADJUSTMENT">ADJUSTMENT (Balance Reconciliation)</option>
                  </select>
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Transaction Reason &amp; Order Details</label>
                  <input
                    type="text"
                    className="saas-form-input"
                    value={topupForm.reason}
                    onChange={(e) => setTopupForm({ ...topupForm, reason: e.target.value })}
                    required
                  />
                </div>

                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Idempotency Reference Key (Anti-Replay)</label>
                  <input
                    type="text"
                    className="saas-form-input"
                    value={topupForm.idempotencyKey}
                    onChange={(e) => setTopupForm({ ...topupForm, idempotencyKey: e.target.value })}
                    required
                    style={{ fontFamily: 'monospace' }}
                  />
                  <span style={{ fontSize: '0.75rem', color: '#64748b' }}>
                    Prevents accidental double-charging or duplicate network retries.
                  </span>
                </div>
              </div>

              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowTopupModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="saas-btn saas-btn-primary" disabled={topupSubmitting}>
                  {topupSubmitting ? 'Processing...' : '⚡ Credit Wallet Account'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Record Payment Modal */}
      {showPaymentModal && selectedInvoice && (
        <div className="saas-modal-backdrop" onClick={() => setShowPaymentModal(false)}>
          <div className="saas-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <div className="saas-modal-header">
              <h3>Record Invoice Payment: {selectedInvoice.invoiceNumber}</h3>
              <button style={{ background: 'none', border: 'none', fontSize: '1.25rem', cursor: 'pointer' }} onClick={() => setShowPaymentModal(false)}>✕</button>
            </div>
            <form onSubmit={handlePaymentSubmit}>
              <div className="saas-modal-body">
                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Amount Paid (₹)</label>
                  <input
                    type="number"
                    step="0.01"
                    className="saas-form-input"
                    value={paymentForm.amountPaid}
                    onChange={(e) => setPaymentForm({ ...paymentForm, amountPaid: e.target.value })}
                    required
                  />
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Payment Channel</label>
                  <select
                    className="saas-form-input"
                    value={paymentForm.paymentMethod}
                    onChange={(e) => setPaymentForm({ ...paymentForm, paymentMethod: e.target.value })}
                  >
                    <option value="BANK_TRANSFER">NEFT / RTGS / IMPS</option>
                    <option value="UPI">UPI Payment</option>
                    <option value="CHEQUE">Cheque / Demand Draft</option>
                    <option value="WALLET_DEDUCTION">Prepaid Platform Credits</option>
                  </select>
                </div>
                <div style={{ marginBottom: '1rem' }}>
                  <label className="saas-form-label">Bank Reference / UTR Number</label>
                  <input
                    type="text"
                    className="saas-form-input"
                    value={paymentForm.transactionRef}
                    onChange={(e) => setPaymentForm({ ...paymentForm, transactionRef: e.target.value })}
                    required
                    style={{ fontFamily: 'monospace' }}
                  />
                </div>
              </div>
              <div className="saas-modal-footer">
                <button type="button" className="saas-btn saas-btn-secondary" onClick={() => setShowPaymentModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="saas-btn saas-btn-success" disabled={paymentSubmitting}>
                  {paymentSubmitting ? 'Recording...' : '✅ Confirm Settlement'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SaasWalletAndUsage;
